import React from "react";
import { Card, Image, Popconfirm, Tag, Row, Col } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { ImageOff } from "lucide-react"; // Lucide icon
import "./product-card.scss";

const ProductCard = ({ product, onEdit, onDelete }) => {
  const { Meta } = Card;

  // Parse variants_data safely
  const parsedVariants = React.useMemo(() => {
    try {
      return product.variants_data ? JSON.parse(product.variants_data) : [];
    } catch (e) {
      console.error("Error parsing variants_data:", e);
      return [];
    }
  }, [product.variants_data]);

  // Find first product image or variant image fallback
  const getMainImage = () => {
    if (product.images && product.images.length > 0) {
      return product.images[0].image;
    } else if (product.variants && product.variants.length > 0) {
      const firstVariantWithImages = product.variants.find(v => v.images && v.images.length > 0);
      if (firstVariantWithImages) {
        return firstVariantWithImages.images[0].image;
      }
    }
    return null; // No images at all
  };

  const mainImage = getMainImage();

  // Render variant images with proper fallbacks
  const renderVariantImages = (variantImages) => {
    if (!variantImages || variantImages.length === 0) {
      return (
        <div className="variant-images-grid">
          <ImageOff size={24} color="#ccc" />
        </div>
      );
    }

    return (
      <div className="variant-images-grid">
        {variantImages.map((img, imgIndex) => (
          <Image
            key={imgIndex}
            src={img.image || img.url || "/placeholder-variant.jpg"}
            width={40}
            height={40}
            preview={false}
          />
        ))}
      </div>
    );
  };

  // Find matching variant with images
  const getVariantWithImages = (variant) => {
    if (!product.variants || !product.variants.length) return null;
    
    return product.variants.find(v => 
      String(v.size) === String(variant.size) && 
      String(v.price) === String(variant.price)
    );
  };

  return (
    <Card
      className="product-card"
      cover={
        <div className="product-image">
          {mainImage ? (
            <Image
              src={mainImage}
              alt={product.title_uz}
              preview={false}
              height={200}
              width="100%"
              style={{ objectFit: "cover" }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "200px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "#f5f5f5",
                borderRadius: "8px"
              }}
            >
              <ImageOff size={48} color="#ccc" />
            </div>
          )}

          <div className="product-actions">
            <div className="action-btn" onClick={onEdit}>
              <EditOutlined />
              <span>Tahrirlash</span>
            </div>
            <Popconfirm
              title={<div className="custom-pop-title">Mahsulotni o'chirish</div>}
              description={
                <div className="custom-pop-description">
                  Siz rostdan ham ushbu mahsulotni o'chirmoqchimisiz?
                </div>
              }
              onConfirm={onDelete}
              okText="O'chirish"
              cancelText="Bekor qilish"
              overlayClassName="custom-popconfirm"
            >
              <div className="action-btn delete">
                <DeleteOutlined />
                <span>O'chirish</span>
              </div>
            </Popconfirm>
          </div>
        </div>
      }
    >
      <Meta
        title={product.title_uz}
        description={
          <>
            <div className="product-description">
              {product.description_uz?.substring(0, 50)}...
            </div>

            {product.is_popular && <Tag color="gold">Ommabop</Tag>}

            {product.has_variants && parsedVariants.length > 0 && (
              <div className="variants-section">
                <h5>Variantlar:</h5>
                {parsedVariants.map((variant, index) => {
                  const variantWithImages = getVariantWithImages(variant);
                  return (
                    <div key={`${variant.size}-${variant.price}-${index}`} className="variant-item">
                      <Row gutter={12} align="middle">
                        <Col span={8}>
                          <strong>O'lcham:</strong> {variant.size}
                        </Col>
                        <Col span={8}>
                          <strong>Narx:</strong> {variant.price} so'm
                        </Col>
                        <Col span={8}>
                          {renderVariantImages(variantWithImages?.images)}
                        </Col>
                      </Row>
                    </div>
                  );
                })}
              </div>
            )}

            {!product.has_variants && product.price && (
              <div className="product-price">
                <strong>Narx:</strong> {product.price} so'm
              </div>
            )}
          </>
        }
      />
    </Card>
  );
};

export default ProductCard;
